import axios from 'axios'
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { refreshAccessToken } from '@/services/auth.service'
import { AUTH_EVENTS, STORAGE_KEYS, TOAST_EVENTS } from '@/constants'
import type { ToastVariant } from '@/context/toast.context'
import { env } from '@/config/env'

// ─── Internal types ─────────────────────────────────────────────────────────

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

type RefreshCallback = (error: unknown, token: string | null) => void

// ─── Token refresh queue ─────────────────────────────────────────────────────
// Prevents multiple simultaneous token refresh calls when concurrent requests
// get a 401. Only one refresh fires; the rest are queued and resolved together.

let isRefreshing = false
let pendingQueue: RefreshCallback[] = []

function flushQueue(error: unknown, token: string | null): void {
  pendingQueue.forEach((cb) => cb(error, token))
  pendingQueue = []
}

// ─── localStorage helpers ────────────────────────────────────────────────────

function readAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
}

function readRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN)
}

function persistTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken)
  localStorage.setItem(STORAGE_KEYS.AUTH_REFRESH_TOKEN, refreshToken)
  localStorage.setItem(STORAGE_KEYS.AUTH_EXPIRES_AT, String(Date.now() + expiresIn * 1000))
}

// ─── Auth event helpers ──────────────────────────────────────────────────────
// Notifies AuthProvider of session changes without importing React context
// (context hooks cannot be called outside the component tree).

function emitLogout(): void {
  window.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGOUT))
}

function emitTokenRefreshed(accessToken: string): void {
  window.dispatchEvent(
    new CustomEvent(AUTH_EVENTS.TOKEN_REFRESHED, { detail: { accessToken } })
  )
}

function emitToast(variant: ToastVariant, title: string, description?: string): void {
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENTS.SHOW, { detail: { variant, title, description } })
  )
}

// ─── Axios instance ──────────────────────────────────────────────────────────

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: { 'Content-Type': 'application/json' },
})

// ─── Request interceptor: inject Bearer token ────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = readAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: unknown) => Promise.reject(error)
)

// ─── Response interceptor: unwrap envelope, handle 401 refresh and global errors ──

// Backend wraps every success response as { data, timestamp } (TransformInterceptor).
// Unwrap it here so services can consume response.data as the raw payload.
function isEnvelope(payload: unknown): payload is { data: unknown; timestamp: string } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'data' in payload &&
    'timestamp' in payload
  )
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (isEnvelope(response.data)) {
      response.data = response.data.data
    }
    return response
  },
  async (error: AxiosError) => {
    const original = error.config as RetryableRequestConfig | undefined

    // Config missing — request was never sent (e.g. cancelled)
    if (!original) {
      return Promise.reject(error)
    }

    // ── 401: attempt token refresh once per original request ──────────────────
    if (error.response?.status === 401 && !original._retry) {
      const storedRefreshToken = readRefreshToken()

      if (!storedRefreshToken) {
        // No refresh token available — force logout
        emitLogout()
        return Promise.reject(error)
      }

      // Another request is already refreshing — queue this one
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          pendingQueue.push((queueError, newToken) => {
            if (queueError) {
              reject(queueError)
            } else {
              original.headers.Authorization = `Bearer ${newToken!}`
              resolve(apiClient(original))
            }
          })
        })
      }

      original._retry = true
      isRefreshing = true

      try {
        const refreshed = await refreshAccessToken(storedRefreshToken)
        persistTokens(refreshed.access_token, refreshed.refresh_token, refreshed.expires_in)
        emitTokenRefreshed(refreshed.access_token)
        flushQueue(null, refreshed.access_token)
        original.headers.Authorization = `Bearer ${refreshed.access_token}`
        return apiClient(original)
      } catch (refreshError: unknown) {
        flushQueue(refreshError, null)
        emitLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // ── 403 Forbidden ─────────────────────────────────────────────────────────
    if (error.response?.status === 403) {
      emitToast('error', 'Acceso denegado', 'No tenés permisos para realizar esta acción.')
    }

    // ── 5xx Server errors ─────────────────────────────────────────────────────
    if (error.response && error.response.status >= 500) {
      emitToast('error', 'Error en el servidor', `Código ${error.response.status}. Intentá de nuevo en unos momentos.`)
    }

    // ── Network error (no response received) ──────────────────────────────────
    if (!error.response) {
      emitToast('error', 'Error de red', 'No se pudo conectar con el servidor. Verificá tu conexión.')
    }

    return Promise.reject(error)
  }
)
